import React, { useEffect, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";
import { api, fmtINR, apiError } from "../lib/api";
import { toast } from "sonner";
import CrudModule from "../components/CrudModule";
import { StockByLocation, StockTransfers } from "../components/StockLocationTabs";
import { MODULES } from "../lib/modules";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const num = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const statusColor = (s) => s === "In Stock" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : s === "Low Stock" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-100 text-red-700 border-red-200";
const BASIS = {
  weighted_average_actual_purchase: "Actual bill + landed cost",
  weighted_average_bill_rate: "Actual bill rate",
  product_master_fallback: "Reference rate (no bill yet)",
  no_transactions: "No transactions",
};

function StockSummary() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/stock-valuation").then((r) => { setRows(r.data.rows); setTotal(r.data.total_stock_value); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const openDetail = async (pid) => {
    try { const { data } = await api.get(`/stock-valuation/${pid}`); setDetail(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cols = [["product", "Product"], ["grade", "Grade"], ["batches", "Batch"], ["unit", "Unit"], ["opening", "Opening"],
    ["purchase_qty", "Purchase"], ["production_qty", "Production"], ["issued_qty", "Sales / Issue"], ["closing_qty", "Closing"],
    ["unit_cost", "Actual Unit Cost"], ["stock_value", "Stock Value"], ["cost_basis", "Cost Basis"]];

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-slate-200 rounded-sm px-4 py-3">
        <span className="text-sm text-slate-600">Valuation: <b>Weighted Average Cost</b> on actual purchase bill rates + expenses marked as landed cost. The product master rate is only a fallback for products never purchased.</span>
        <span className="text-sm whitespace-nowrap">Total Stock Value <b className="font-mono" data-testid="total-stock-value">{fmtINR(total)}</b></span>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="stock-summary-table">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{cols.map((c) => <th key={c[0]} className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{c[1]}</th>)}<th /></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : !rows.length ? <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-slate-400">No products</td></tr>
                : rows.map((r) => (
                  <tr key={r.product_id} data-testid={`stock-row-${r.product_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-3 font-medium text-slate-800 whitespace-nowrap">{r.product}</td>
                    <td className="px-3 py-3 text-slate-600">{r.grade || "-"}</td>
                    <td className="px-3 py-3 text-slate-500 text-xs max-w-[140px] truncate">{(r.batches || []).join(", ") || "-"}</td>
                    <td className="px-3 py-3 text-slate-600">{r.unit}</td>
                    {["opening", "purchase_qty", "production_qty", "issued_qty", "closing_qty"].map((k) => (
                      <td key={k} className="px-3 py-3 font-mono tabular-nums text-slate-900">{num(r[k])}</td>))}
                    <td className="px-3 py-3 font-mono tabular-nums text-slate-900" data-testid={`unit-cost-${r.product_id}`}>{fmtINR(r.unit_cost)}</td>
                    <td className="px-3 py-3 font-mono tabular-nums font-semibold text-slate-900" data-testid={`stock-value-${r.product_id}`}>{fmtINR(r.stock_value)}</td>
                    <td className="px-3 py-3"><Badge variant="outline" className="rounded-full px-2.5 text-[10px] bg-slate-50 text-slate-600">{BASIS[r.cost_basis] || r.cost_basis}</Badge></td>
                    <td className="px-3 py-3 text-right"><Button data-testid={`stock-audit-${r.product_id}`} size="sm" variant="outline" className="rounded-sm h-8 gap-1" onClick={() => openDetail(r.product_id)}><Search className="w-3.5 h-3.5" /> Trace</Button></td>
                  </tr>))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="stock-audit-dialog">
          <DialogHeader><DialogTitle className="font-head">How this stock value was calculated · {detail?.product}</DialogTitle>
            <DialogDescription>{detail?.audit?.explanation}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Closing Qty", num(detail?.closing_qty)], ["Actual Unit Cost", fmtINR(detail?.unit_cost)],
              ["Stock Value", fmtINR(detail?.stock_value)], ["Cost of Issues", fmtINR(detail?.cost_of_issues)]].map(([l, v]) => (
                <div key={l} className="bg-white border border-slate-200 rounded-sm p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="text-base font-mono font-semibold mt-1 text-slate-900">{v}</div>
                </div>))}
          </div>
          <p className="text-xs text-slate-500">{detail?.audit?.note} · Reference purchase rate on the product master: <b>{fmtINR(detail?.reference_purchase_rate)}</b> (not used for these transactions)</p>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                {["Date", "Type", "Bill No", "Supplier", "Batch", "Qty", "Purchase Rate", "Landed Cost", "Final Unit Cost", "Total Cost", "Remaining", "Running Avg"].map((h) => (
                  <th key={h} className="px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody>
                {!(detail?.receipts || []).length ? <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-400">No stock receipts yet</td></tr>
                  : detail.receipts.map((r, i) => (
                    <tr key={i} data-testid={`audit-receipt-${i}`} className="border-b border-slate-100">
                      <td className="px-2 py-2 whitespace-nowrap">{r.date}</td>
                      <td className="px-2 py-2 text-slate-500">{r.movement_type}</td>
                      <td className="px-2 py-2">{r.bill_no}</td>
                      <td className="px-2 py-2">{r.supplier}</td>
                      <td className="px-2 py-2">{r.batch}</td>
                      <td className="px-2 py-2 font-mono">{num(r.quantity)}</td>
                      <td className="px-2 py-2 font-mono">{r.purchase_rate ? fmtINR(r.purchase_rate) : "-"}</td>
                      <td className="px-2 py-2 font-mono">{fmtINR(r.landed_cost)}</td>
                      <td className="px-2 py-2 font-mono font-semibold">{fmtINR(r.final_unit_cost)}</td>
                      <td className="px-2 py-2 font-mono">{fmtINR(r.total_cost)}</td>
                      <td className="px-2 py-2 font-mono">{num(r.remaining)}</td>
                      <td className="px-2 py-2 font-mono text-slate-500">{fmtINR(r.running_avg_cost)}</td>
                    </tr>))}
              </tbody>
            </table>
          </div>

          {!!(detail?.issues || []).length && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Date", "Type", "Reference", "Qty Issued", "Unit Cost Used", "Cost Value"].map((h) => (
                    <th key={h} className="px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
                </tr></thead>
                <tbody>
                  {detail.issues.map((r, i) => (
                    <tr key={i} data-testid={`audit-issue-${i}`} className="border-b border-slate-100">
                      <td className="px-2 py-2 whitespace-nowrap">{r.date}</td>
                      <td className="px-2 py-2 text-slate-500">{r.movement_type}</td>
                      <td className="px-2 py-2">{r.reference}</td>
                      <td className="px-2 py-2 font-mono">{num(r.quantity)}</td>
                      <td className="px-2 py-2 font-mono">{fmtINR(r.unit_cost_used)}</td>
                      <td className="px-2 py-2 font-mono">{fmtINR(r.cost_value)}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MovementStatus() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/stock-summary").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const low = rows.filter((r) => r.status !== "In Stock");
  if (!low.length) return null;
  return <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="low-stock-alert">{low.length} product(s) low or out of stock: {low.slice(0, 6).map((r) => r.product).join(", ")}</div>;
}

export default function InventoryPage() {
  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Inventory</h1>
        <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Stock and value are calculated from actual posted movements and purchase bill rates (decimal MT supported)</p>
      </div>
      <Tabs defaultValue="summary">
        <TabsList className="bg-white border border-slate-200 rounded-sm mb-5">
          <TabsTrigger value="summary" data-testid="inv-tab-summary">Stock &amp; Valuation</TabsTrigger>
          <TabsTrigger value="location" data-testid="inv-tab-location">Stock by Location</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="inv-tab-transfers">Stock Transfers</TabsTrigger>
          <TabsTrigger value="movements" data-testid="inv-tab-movements">Movements</TabsTrigger>
        </TabsList>
        <TabsContent value="summary"><MovementStatus /><StockSummary /></TabsContent>
        <TabsContent value="location"><StockByLocation /></TabsContent>
        <TabsContent value="transfers"><StockTransfers /></TabsContent>
        <TabsContent value="movements"><CrudModule config={MODULES.inventory} /></TabsContent>
      </Tabs>
    </div>
  );
}
